"use client";

import { useAuth } from "@clerk/nextjs";
import { useUserSettings } from "@sukha/query";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import { isUiLocale } from "@sukha/shared/locale/messageLocale";
import {
  clearLocaleCookie,
  getLocaleCookie,
  setLocaleCookie,
} from "@/lib/i18n/localeCookie";
import { refreshMemberFocusMessages } from "@/hooks/focusMessages/useFocusMessagesQuery";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Keeps the locale preference cookie aligned with user_settings.locale for SSR,
 * and clears the cookie on sign-out so the next session is not affected.
 */
export function LocaleSync(): null {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useAuth();
  const { data: userSettings } = useUserSettings();
  const previousLocaleRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      if (getLocaleCookie()) {
        clearLocaleCookie();
        router.refresh();
      }
      return;
    }

    if (!userSettings?.locale || !isUiLocale(userSettings.locale)) {
      return;
    }

    const dbLocale = userSettings.locale;
    if (previousLocaleRef.current && previousLocaleRef.current !== dbLocale) {
      refreshMemberFocusMessages(queryClient);
    }
    previousLocaleRef.current = dbLocale;

    if (getLocaleCookie() === dbLocale) {
      return;
    }

    setLocaleCookie(dbLocale);
    router.refresh();
  }, [isLoaded, isSignedIn, userSettings?.locale, router, queryClient]);

  return null;
}
